import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient, formatApiError } from "@/lib/auth";
import FinanceLayout, { fmtUsd } from "./FinanceLayout";
import { Wallet, ArrowUp, ArrowDown, ShieldAlert, PiggyBank, Loader2 } from "lucide-react";
import { toast } from "sonner";

const OverviewPage = () => {
  const [summary, setSummary] = useState(null);
  const [reserve, setReserve] = useState(null);
  const [expenses, setExpenses] = useState({ pending_dual_approval: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [d, r, e] = await Promise.all([
          apiClient.get("/finance/distributions/summary"),
          apiClient.get("/finance/reserve/summary"),
          apiClient.get("/finance/expenses?status=pending_dual_approval"),
        ]);
        setSummary(d.data);
        setReserve(r.data);
        setExpenses(e.data);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <FinanceLayout>
      <div className="mb-8">
        <div className="mono-label mb-2">// FINANCIAL OVERVIEW</div>
        <h1 className="serif-head text-3xl md:text-4xl">Money at a glance</h1>
        <p className="text-nex-chrome mt-2 text-sm">
          Year-to-date {summary?.year || new Date().getFullYear()} · governance rules active
        </p>
      </div>

      {loading ? (
        <div className="text-center py-20"><Loader2 className="animate-spin text-nex-gold mx-auto" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="finance-overview-metrics">
            <MetricCard
              Icon={ArrowUp}
              label="Revenue YTD"
              value={fmtUsd(summary?.revenue)}
              sub="income transactions"
              accent="emerald"
              testid="metric-revenue"
            />
            <MetricCard
              Icon={ArrowDown}
              label="Expenses YTD"
              value={fmtUsd(summary?.expenses)}
              sub="approved only"
              accent="silver"
              testid="metric-expenses"
            />
            <MetricCard
              Icon={PiggyBank}
              label="Reserve Balance"
              value={fmtUsd(reserve?.balance)}
              sub={`10% of every income line`}
              accent="gold"
              testid="metric-reserve"
            />
            <MetricCard
              Icon={Wallet}
              label="Distributable"
              value={fmtUsd(summary?.net_distributable)}
              sub={`${fmtUsd(summary?.per_partner_share)} / partner`}
              accent="gold"
              testid="metric-distributable"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {/* Pending governance */}
            <div className={`card-industrial corner-tick p-6 ${expenses.pending_dual_approval > 0 ? "border-amber-500/40" : ""}`}
                 data-testid="pending-governance">
              <div className="flex items-center gap-3 mb-4">
                <ShieldAlert size={20} className={expenses.pending_dual_approval > 0 ? "text-amber-300" : "text-nex-chrome"} />
                <div className="mono-label">// GOVERNANCE</div>
              </div>
              <h2 className="serif-head text-2xl mb-2">
                {expenses.pending_dual_approval} pending dual approval
              </h2>
              <p className="text-nex-chrome text-sm mb-4">
                Any expense over ${expenses.threshold_usd?.toLocaleString?.() || "1,000"} needs both partners to sign off.
              </p>
              <Link to="/admin/finance/expenses" className="tick-btn-secondary text-xs !py-2 !px-4 inline-flex">
                Review queue →
              </Link>
            </div>

          </div>

          <div className="card-industrial corner-tick p-6" data-testid="pl-snapshot">
            <div className="mono-label mb-2">// P&L SNAPSHOT</div>
            <h2 className="serif-head text-xl mb-4">Year-to-date</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
              <Stat label="Revenue"      value={fmtUsd(summary?.revenue)} />
              <Stat label="COGS (20%)"   value={fmtUsd(summary?.cogs)} muted />
              <Stat label="Reserve (10%)" value={fmtUsd(summary?.reserve)} muted />
              <Stat label="Op Expenses"  value={fmtUsd(summary?.expenses)} muted />
              <Stat label="Net Distributable" value={fmtUsd(summary?.net_distributable)} accent="gold" />
            </div>
          </div>
        </>
      )}
    </FinanceLayout>
  );
};

const MetricCard = ({ Icon, label, value, sub, accent = "silver", testid }) => {
  const accentClass = {
    silver:  "text-nex-silver",
    emerald: "text-emerald-300",
    red:     "text-red-300",
    gold:    "text-nex-gold",
  }[accent] || "text-nex-silver";
  return (
    <div className="card-industrial p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="mono-label !text-[9px]">{label}</div>
        <Icon size={14} className={accentClass} />
      </div>
      <div className={`serif-head text-3xl ${accentClass}`}>{value}</div>
      {sub && <div className="mono-body text-[10px] text-nex-muted mt-2">{sub}</div>}
    </div>
  );
};

const Stat = ({ label, value, muted, accent }) => (
  <div>
    <div className="mono-label !text-[9px] mb-1">{label}</div>
    <div className={`font-medium text-lg ${accent === "gold" ? "text-nex-gold" : muted ? "text-nex-chrome" : "text-nex-silver"}`}>
      {value}
    </div>
  </div>
);

export default OverviewPage;
